import React, { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import "../styles/Dashboard.css";
import { deleteData, getData, postData, putData } from "../services/Request";
import { toast } from "react-toastify";
import { useLoader } from "../context/LoaderContext";
import { PROVINCE_OPTIONS } from "../constants/provinceName";
import Select from "react-select";

const provinceOptions = PROVINCE_OPTIONS.map((p) => ({
  value: p,
  label: p,
}));
const emptyForm = {
  buyer_name: "",
  cnic: "",
  province: "",
  address: "",
  registration_type: "",
  type: "person",
};

const sanitizeCnic = (value) =>
  `${value || ""}`.replace(/\D/g, "").slice(0, 13);

const hasValue = (value) => `${value ?? ""}`.trim().length > 0;

const formatCnic = (value) => {
  const digits = sanitizeCnic(value);
  const part1 = digits.slice(0, 5);
  const part2 = digits.slice(5, 12);
  const part3 = digits.slice(12, 13);
  if (digits.length <= 5) return part1;
  if (digits.length <= 12) return `${part1}-${part2}`;
  return `${part1}-${part2}-${part3}`;
};

const formatId = (value, type) =>
  `${type || ""}`.toLowerCase() === "company"
    ? `${value || ""}`
    : formatCnic(value);

const formatBuyerType = (type) => {
  const value = `${type || ""}`.toLowerCase();
  if (value === "company") return "Company";
  if (value === "person") return "Person";
  return "Person";
};

const getBuyerId = (buyer) => buyer?.id ?? buyer?._id ?? "";

const normaliseList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.buyers)) return payload.buyers;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getErrorMessage = (error, fallback = "Something went wrong.") =>
  error?.response?.data?.message ||
  error?.data?.message ||
  error?.message ||
  fallback;

export default function Buyers() {
  const { showLoader, hideLoader } = useLoader();
  const [buyers, setBuyers] = useState([]);
  const [totalBuyers, setTotalBuyers] = useState(0);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const hasFetchedInitial = useRef(false);
  const startIndex = useMemo(() => (page - 1) * pageSize, [page, pageSize]);
  const isPerson = formData.type === "person";
  const isCnicComplete = formData.cnic.length === 13;
  const isIdValid = isPerson ? isCnicComplete : formData.cnic.length > 0;
  const isFormComplete =
    hasValue(formData.buyer_name) &&
    hasValue(formData.province) &&
    hasValue(formData.registration_type) &&
    isIdValid;

  const fetchBuyers = async ({ withLoader = false } = {}) => {
    try {
      if (withLoader) {
        showLoader();
      } else {
        setListLoading(true);
      }
      const response = await getData(
        `api/buyer?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(
          search,
        )}`,
      );
      const list = normaliseList(response);
      setBuyers(list);
      const pagination =
        response?.pagination || response?.data?.pagination || {};
      setTotalPages(pagination?.totalPages || 1);
      const totalFromResponse =
        pagination?.total ??
        pagination?.totalItems ??
        pagination?.totalRecords ??
        pagination?.count ??
        response?.total ??
        response?.data?.total;
      const parsedTotal =
        typeof totalFromResponse === "string"
          ? Number(totalFromResponse)
          : totalFromResponse;
      setTotalBuyers(Number.isFinite(parsedTotal) ? parsedTotal : list.length);
    } catch (error) {
      toast.error("Failed to fetch buyers, please try again.");
      console.error("Failed to fetch buyers", error);
    } finally {
      if (withLoader) {
        hideLoader();
      } else {
        setListLoading(false);
      }
    }
  };

  useEffect(() => {
    if (hasFetchedInitial.current) return;
    hasFetchedInitial.current = true;
    fetchBuyers({ withLoader: true });
  }, []);

  useEffect(() => {
    fetchBuyers();
  }, [page, search]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === "cnic" ? sanitizeCnic(value) : value;
    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isIdValid) {
      toast.error(
        isPerson ? "CNIC must be exactly 13 digits." : "NTN is required.",
      );
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await putData(`api/buyer/${editingId}`, formData);
        toast.success("Buyer updated.");
      } else {
        await postData("api/buyer", formData);
        toast.success("New Buyer Added.");
      }
      resetForm();
      await fetchBuyers();
    } catch (error) {
      const message = getErrorMessage(
        error,
        editingId ? "Buyer not updated." : "Buyer not added.",
      );
      toast.error(message);
      console.error("Submit buyer failed", error);
    } finally {
      setSubmitting(false);
    }
  };

  const loadBuyerForEdit = async (buyerId) => {
    if (!buyerId) {
      toast.error("Buyer ID not found.");
      return;
    }
    try {
      showLoader();
      const response = await getData(`api/buyer/${buyerId}`);
      const payload =
        response?.data && !Array.isArray(response.data)
          ? response.data
          : response;
      setFormData({
        ...emptyForm,
        ...payload,
        cnic: sanitizeCnic(payload?.cnic),
        type: payload?.type || "person",
      });
      setEditingId(buyerId);
    } catch (error) {
      toast.error("Buyer details not load.");
      console.error("Failed to fetch single buyer", error);
    } finally {
      hideLoader();
    }
  };

  const handleDelete = async (buyerId) => {
    const targetId = buyerId || confirmDeleteId;
    if (!targetId) {
      toast.error("Buyer ID not received.");
      return;
    }
    setDeletingId(targetId);
    const toastId = toast.loading("Deleting buyer...");
    try {
      showLoader();
      await deleteData(`api/buyer/${targetId}`);
      toast.update(toastId, {
        render: "Buyer deleted.",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
      if (editingId === targetId) {
        resetForm();
      }
      await fetchBuyers();
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Buyer could not be deleted. Please try again.",
      );
      toast.update(toastId, {
        render: message,
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
      console.error("Failed to delete buyer", error);
    } finally {
      hideLoader();
      setConfirmDeleteId(null);
      setDeletingId(null);
    }
  };

  const openDeleteModal = (buyerId) => setConfirmDeleteId(buyerId);
  const closeDeleteModal = () => setConfirmDeleteId(null);

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="app-content">
        <Navbar />
        <div className="dashboard-page">
          <main className="dashboard">
            <section className="sample-page buyer-page">
              <div className="sample-page__header">
                <h1>Buyers</h1>
              </div>

              <div className="buyer-management">
                <div className="seller-card seller-form-card">
                  <div className="seller-card__header">
                    <h3>{editingId ? "Update Buyer" : "Add New Buyer"}</h3>
                    {editingId && (
                      <button
                        type="button"
                        className="text-link-btn"
                        onClick={resetForm}
                      >
                        Cancel edit
                      </button>
                    )}
                  </div>
                  <form className="seller-form" onSubmit={handleSubmit}>
                    <div className="form-field">
                      <span>Type</span>
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "center",
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.type === "person"}
                            onChange={() =>
                              setFormData((prev) => ({
                                ...prev,
                                type: "person",
                              }))
                            }
                          />
                          <span>Person</span>
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.type === "company"}
                            onChange={() =>
                              setFormData((prev) => ({
                                ...prev,
                                type: "company",
                              }))
                            }
                          />
                          <span>Company</span>
                        </label>
                      </div>
                    </div>

                    <div className="form-grid">
                      <label className="form-field">
                        <span>
                          {isPerson ? "Buyer Name" : "Company Name"}
                          <span className="requied">*</span>
                        </span>

                        <input
                          type="text"
                          name="buyer_name"
                          value={formData.buyer_name}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter buyer name"
                        />
                      </label>

                      <label className="form-field">
                        <span>
                          {isPerson ? "CNIC / NTN" : "NTN"}
                          <span className="requied">*</span>
                        </span>

                        <input
                          type="text"
                          name="cnic"
                          value={formatId(formData.cnic, formData.type)}
                          onChange={handleInputChange}
                          required
                          maxLength="15"
                          inputMode="numeric"
                          placeholder={
                            isPerson ? "32202-6124239-1" : "Enter NTN"
                          }
                        />
                        {!isIdValid && formData.cnic.length > 0 && (
                          <small style={{ color: "red" }}>
                            {isPerson
                              ? "CNIC must be 13 digits."
                              : "NTN is required."}
                          </small>
                        )}
                      </label>

                      <label className="form-field">
                        <span>
                          Province<span className="requied">*</span>
                        </span>

                        <Select
                          options={provinceOptions}
                          placeholder="Select Province"
                          isSearchable={true}
                          value={provinceOptions.find(
                            (opt) => opt.value === formData.province,
                          )}
                          onChange={(selectedOption) =>
                            setFormData({
                              ...formData,
                              province: selectedOption.value,
                            })
                          }
                        />
                      </label>
                    </div>

                    <label className="form-field">
                      <span>Address</span>
                      <textarea
                        name="address"
                        rows="3"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Office / billing address"
                      />
                    </label>

                    <label className="form-field">
                      <span>
                        Registration Type<span className="requied">*</span>
                      </span>

                      <select
                        name="registration_type"
                        value={formData.registration_type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select registration type</option>
                        <option value="Registered">Registered</option>
                        <option value="Unregistered">Unregistered</option>
                      </select>
                    </label>

                    <button
                      type="submit"
                      className="primary-btn"
                      disabled={submitting || !isFormComplete}
                    >
                      {submitting
                        ? "Please wait..."
                        : editingId
                          ? "Update buyer"
                          : "Add buyer"}
                    </button>
                  </form>
                </div>

                <div className="seller-card seller-table-card">
                  <div className="seller-card__header">
                    <h3>Buyer list</h3>
                    <button
                      type="button"
                      className="text-link-btn"
                      onClick={() => fetchBuyers()}
                      disabled={listLoading}
                    >
                      {listLoading ? "Refreshing..." : "Refresh"}
                    </button>
                  </div>
                  <div
                    className="list-actions"
                    style={{ marginBottom: "10px" }}
                  >
                    <input
                      type="search"
                      placeholder="Search buyers"
                      value={search}
                      onChange={(e) => {
                        setPage(1);
                        setSearch(e.target.value);
                      }}
                    />
                  </div>
                  <div className="seller-table-wrapper">
                    <table className="seller-table">
                      <thead>
                        <tr>
                          <th>No.</th>
                          <th>Name</th>
                          <th>Type</th>
                          <th>CNIC/NTN</th>
                          <th>Province</th>
                          <th>Registration</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {buyers.length === 0 && (
                          <tr>
                            <td colSpan="8" className="empty-state">
                              {listLoading
                                ? "Loading buyers..."
                                : "No buyer list."}
                            </td>
                          </tr>
                        )}
                        {buyers.map((buyer, index) => {
                          const buyerId = getBuyerId(buyer);
                          const rowKey =
                            buyerId ?? `${buyer?.cnic ?? "row"}-${index}`;
                          return (
                            <tr key={rowKey}>
                              <td>{startIndex + index + 1}</td>
                              <td>{buyer?.buyer_name || "-"}</td>
                              <td>{formatBuyerType(buyer?.type)}</td>
                              <td>
                                {formatId(buyer?.cnic, buyer?.type) || "-"}
                              </td>
                              <td>{buyer?.province || "-"}</td>
                              <td>
                                {buyer?.registration_type || (
                                  <span className="muted-text">N/A</span>
                                )}
                              </td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    type="button"
                                    onClick={() => loadBuyerForEdit(buyerId)}
                                    disabled={!buyerId}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="danger"
                                    onClick={() => openDeleteModal(buyerId)}
                                    disabled={!buyerId}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-pagination">
                    <button
                      type="button"
                      disabled={page <= 1 || listLoading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    <span>
                      Page {page} of {totalPages}
                    </span>
                    <span>Total: {totalBuyers}</span>
                    <button
                      type="button"
                      disabled={page >= totalPages || listLoading}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                  {confirmDeleteId && (
                    <div className="modal-backdrop">
                      <div className="modal-card">
                        <h3>Delete Buyer</h3>
                        <p>This action cannot be undone. Delete this buyer?</p>
                        <div className="modal-actions">
                          <button
                            type="button"
                            className="outline-btn"
                            onClick={closeDeleteModal}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="primary-btn danger"
                            onClick={() => handleDelete(confirmDeleteId)}
                            disabled={deletingId !== null}
                          >
                            {deletingId === confirmDeleteId ? (
                              <>
                                <span
                                  className="btn-spinner"
                                  aria-hidden="true"
                                />
                                Deleting...
                              </>
                            ) : (
                              "Delete"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
